import { useState, useCallback } from 'react';

/**
 * Custom hook for managing search expansion state
 * 
 * This hook provides a simple way to track when compact search is expanded
 * and allows other components to react to this state change.
 * 
 * @returns Object containing search state and handlers
 */
export const useSearchState = () => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // Handler for search expansion change
  const handleSearchExpandChange = useCallback((expanded: boolean) => {
    setIsSearchExpanded(expanded);
  }, []);

  // Helper function to get conditional classes for hiding elements
  const getHiddenClasses = useCallback((
    hideWhenExpanded: boolean = true,
    customClasses: string = ''
  ) => {
    const baseClasses = 'transition-all duration-300';
    const hiddenClasses = hideWhenExpanded && isSearchExpanded 
      ? 'opacity-0 scale-95 pointer-events-none' 
      : 'opacity-100 scale-100';
    
    return `${baseClasses} ${hiddenClasses} ${customClasses}`.trim();
  }, [isSearchExpanded]);

  // Helper function for display none approach
  const getDisplayClasses = useCallback((
    hideWhenExpanded: boolean = true,
    customClasses: string = ''
  ) => {
    const hiddenClasses = hideWhenExpanded && isSearchExpanded ? 'hidden' : 'block';
    return `${hiddenClasses} ${customClasses}`.trim();
  }, [isSearchExpanded]);

  return {
    isSearchExpanded,
    setIsSearchExpanded,
    handleSearchExpandChange,
    getHiddenClasses,
    getDisplayClasses,
  };
};

export default useSearchState;
